import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import cn from 'classnames';
import Swiper from 'swiper';
import {Swiper as SwiperType} from 'swiper/types';

import {useCarouselSSRStyle} from 'coreHooks';

import {setRef} from 'brand/app/utils/refs';

import {ICarouselProps} from '../../ICarousel';

import {CarouselControllerContextProvider, useCarouselControllerContext} from './CarouselSwiperContext';
import {getMappedSwiperConfig, setupSwiperConfig} from './helpers';
import {CarouselNavigation, CarouselPaginationNumbers} from './Subcomponents';

// eslint-disable-next-line no-restricted-imports
import './index.scss';

const CarouselSwiper = ({
	classNames,
	children,
	config,
	handlers,
	disableNavigationForOneSlide,
	tresholdInit = 0.1,
	ref,
}: ICarouselProps) => {
	if (tresholdInit <= 0) {
		throw new Error('tresholdInit value must > 0');
	}

	// This Context is used to overwrite Contentful carousel config
	const {controllerConfig, liftSliderInstance} = useCarouselControllerContext(true) || {};

	const childrenArray = Array.from(children || []);
	const currentConfig = useMemo(() => (
		setupSwiperConfig(controllerConfig || config, disableNavigationForOneSlide, childrenArray, classNames)
	), [controllerConfig, config, disableNavigationForOneSlide, childrenArray.length, classNames]);

	const swiperRef = useRef<SwiperType>(null);
	const swiperContainerRef = useRef(null);
	const resizeTimeoutRef = useRef(null);
	const [additionalWrapperClasses, setAdditionalWrapperClasses] = useState([]);
	const [isReachBeginning, setIsReachBeginning] = useState<boolean>(false);
	const [isReachEnd, setIsReachEnd] = useState<boolean>(false);
	const [isInitialized, setIsInitialized] = useState<boolean>(false);
	const {className} = useCarouselSSRStyle(currentConfig);

	useEffect(() => {
		const iObserver = new IntersectionObserver(
			(entries, observer) => {
				entries.forEach((e) => {
					if (e.isIntersecting && swiperContainerRef.current) {
						if (!swiperRef.current || !swiperRef.current.initialized) {
							swiperRef.current = new Swiper(
								swiperContainerRef.current,
								getMappedSwiperConfig(currentConfig)
							);

							handleSwiperRef(swiperRef.current);
							handleOnInit();

							swiperRef.current.on('slideChange', handleSlideChange);
							swiperRef.current.on('reachEnd', handleSlideChange);
							swiperRef.current.on('reachBeginning', handleSlideChange);
						}

						window.addEventListener('resize', resizeHandler, {
							passive: true
						});
						observer.unobserve(e.target);
					}
				});
			},
			{
				rootMargin: '0px 0px -200px 0px',
				threshold: tresholdInit,
			},
		);

		iObserver.observe(swiperContainerRef.current);

		const resizeHandler = () => {
			clearTimeout(resizeTimeoutRef.current);

			resizeTimeoutRef.current = setTimeout(() => {
				if (swiperRef.current && swiperRef.current.initialized) {
					swiperRef.current.update();
				}
			}, 300);
		};

		return () => {
			if (swiperRef.current && swiperRef.current.el) {
				iObserver.unobserve(swiperRef.current.el);
			}

			window.removeEventListener('resize', resizeHandler);
		};
	}, []);

	useEffect(() => {
		if (swiperRef.current) {
			swiperRef.current.params.slidesPerView = currentConfig.slidesPerView;

			if (children.length < swiperRef.current.activeIndex) {
				swiperRef.current.slideTo(0);
			}

			swiperRef.current.update();
		}
	}, [children.length, currentConfig.slidesPerView]);

	const handleOnInit = useCallback(() => {
		setIsInitialized(true);

		if (typeof handlers?.onInitHandler === 'function') {
			handlers.onInitHandler(swiperRef.current);
		}
	}, [!!handlers?.onInitHandler]);

	const handleSwiperRef = useCallback((s: SwiperType) => {
		if (!s) {
			return;
		}

		swiperRef.current = s;

		// Send the swiper instance to the parent component
		setRef(ref, s);

		if (typeof currentConfig.setThumbsSwiper === 'function') {
			currentConfig.setThumbsSwiper(s);
		}

		// Send the swiper instance to the grandparent component through the CarouselControllerContext
		if (typeof liftSliderInstance === 'function') {
			liftSliderInstance(s);
		}

		handleSlideChange(s);
	}, []);

	const handleSlideChange = useCallback(
		(swiper?: SwiperType) => {
			if (!swiper) {
				return;
			}

			const isBeginning = swiper.isBeginning;
			const isEnd = swiper.isEnd;

			if (!currentConfig.loop) {
				setIsReachBeginning(isBeginning);
				setIsReachEnd(isEnd);
			}

			let newWrapperClasses = [];

			if (isEnd) {
				newWrapperClasses.push('swiper-end');
			} else if (isBeginning) {
				newWrapperClasses.push('swiper-beginning');
			}

			setAdditionalWrapperClasses(newWrapperClasses);

			if (typeof handlers?.onChangeHandler === 'function') {
				handlers.onChangeHandler(swiperRef.current);
			}
		},
		[currentConfig.loop, !!handlers?.onChangeHandler],
	);

	const getSlides = () => {
		// Double the items to make the carousel infinite
		if (
			currentConfig.loop &&
			currentConfig.duplicateSlides.enabled &&
			childrenArray.length < currentConfig.duplicateSlides.slidesNumber
		) {
			const slidesElem = [...childrenArray];

			while (slidesElem.length < currentConfig.duplicateSlides.slidesNumber) {
				slidesElem.push(...childrenArray);
			}

			return slidesElem;
		} else {
			return childrenArray;
		}
	};

	return (
		<CarouselControllerContextProvider value={{
			isInCarousel: true,
			swiperRef: swiperRef
		}}>
			{currentConfig.navigationConfig?.enabled && (
				<CarouselNavigation
					swiperRef={swiperRef}
					currentConfig={currentConfig}
					classNames={{
						wrapper: cn(classNames?.navigation, {'swiper-navigation-initialized': isInitialized}),
						previousButton: cn({
							'swiper-navigation__btn--reach-beginning': isReachBeginning,
						}),
						nextButton: cn({
							'swiper-navigation__btn--reach-end': isReachEnd,
						}),
					}}
				/>
			)}

			<div
				className={cn(
					'swiper',
					{
						[config.customClassName]: config.customClassName,
						'swiper-3d': config.effect === 'coverflow'
					}
				)}
				ref={swiperContainerRef}
			>
				<div className={cn('swiper-wrapper', classNames?.swiper, ...additionalWrapperClasses, {
					'swiper-wrapper--reach-beginning': isReachBeginning,
					[className]: className
				})}>
					{getSlides().map((slide, index) => {
						if (slide?.elem) {
							return (
								<div
									className={cn(classNames?.slide, 'swiper-slide')}
									key={`${slide.key}-${index}`}
								>
									{slide.elem}
								</div>
							);
						}
					})}
				</div>
				<div className={cn(classNames?.pagination, 'pagination-container')}>
					<div className="pagination-wrapper"/>
					{swiperRef.current && currentConfig.enablePaginationNumbers && childrenArray.length > 1 && (
						<CarouselPaginationNumbers
							swiperRef={swiperRef}
							slidesCount={childrenArray.length}
							plainNumeration={currentConfig.plainNumeration}
						/>
					)}
				</div>
				{currentConfig.scrollBarConfig && (
					<div className="swiper-scrollbar"/>
				)}
			</div>
		</CarouselControllerContextProvider>
	);
};

export default CarouselSwiper;
